import type { IntakeDocument } from '@/components/documents/DocumentsIntake';
import type {
  BankIncomingAdminLine,
  FundCostTemplate,
} from '@/types/bank-reconciliation';
import type { FundCost, FundCostCategory } from '@/types/fund-costs';
import type { FundTransactionDocumentLinkRequest } from '@/types/fund-transactions';
import { getTodayAsISODate } from '@/utils/date-operations';
import { toNumber } from '@/utils/number-operations';

export interface FundCostWindowFormState {
  effective_from: string;
  effective_to: string;
  deferred: boolean;
  cost_category: FundCostCategory | '';
  description: string;
  notes: string;
}

/** What a cost entered by hand also needs; a bank line supplies it otherwise. */
export interface FundCostPaymentFormState {
  amount: number | null;
  counterparty_name: string;
  counterparty_iban: string;
}

export const fundCostsPath = (fundId: string) =>
  `/app/funds/${fundId}?tab=transactions&section=fees-and-costs&subsection=fund-costs`;

export const fundCostDesignatePath = (fundId: string, incomingId: string) =>
  `/app/funds/${fundId}/fund-costs/designate/${incomingId}`;

export const EMPTY_PAYMENT: FundCostPaymentFormState = {
  amount: null,
  counterparty_name: '',
  counterparty_iban: '',
};

export function emptyWindow(): FundCostWindowFormState {
  const today = getTodayAsISODate();
  return {
    effective_from: today,
    effective_to: today,
    deferred: false,
    cost_category: '',
    description: '',
    notes: '',
  };
}

export function windowFromCost(cost: FundCost): FundCostWindowFormState {
  return {
    effective_from: cost.effective_from,
    effective_to: cost.effective_to,
    deferred: cost.effective_to !== cost.effective_from,
    cost_category: cost.cost_category,
    description: cost.description ?? '',
    notes: cost.notes ?? '',
  };
}

export function paymentFromCost(cost: FundCost): FundCostPaymentFormState {
  return {
    amount: toNumber(cost.amount),
    counterparty_name: cost.counterparty_name ?? '',
    counterparty_iban: cost.counterparty_iban ?? '',
  };
}

function todayYearMonth(): [number, number] {
  return getTodayAsISODate().slice(0, 7).split('-').map(Number) as [
    number,
    number,
  ];
}

// Works from the ISO string throughout - reading a UTC-midnight Date back
// with local getters shifts the 1st into the previous month west of UTC.
function monthBounds(isoDate: string | null): { from: string; to: string } {
  const [year, month] = isoDate
    ? (isoDate.slice(0, 7).split('-').map(Number) as [number, number])
    : todayYearMonth();
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const pad = (value: number) => String(value).padStart(2, '0');
  return {
    from: `${year}-${pad(month)}-01`,
    to: `${year}-${pad(month)}-${pad(lastDay)}`,
  };
}

export function windowFromLine(
  line: BankIncomingAdminLine
): FundCostWindowFormState {
  const { from, to } = monthBounds(line.value_date);
  return {
    effective_from: from,
    effective_to: to,
    deferred: false,
    cost_category: '',
    description: line.counterparty_name ?? '',
    notes: '',
  };
}

export function windowFromTemplate(
  template: FundCostTemplate
): FundCostWindowFormState {
  return {
    effective_from: template.effective_from,
    effective_to: template.effective_to,
    deferred: template.effective_to !== template.effective_from,
    cost_category: template.cost_category,
    description: template.description ?? '',
    notes: template.notes ?? '',
  };
}

/** Call only after validation: the category is known to be picked. */
export function windowPayload(state: FundCostWindowFormState) {
  return {
    cost_category: state.cost_category as FundCostCategory,
    description: state.description || null,
    effective_from: state.effective_from,
    effective_to: state.deferred ? state.effective_to : state.effective_from,
    notes: state.notes || null,
  };
}

export function paymentPayload(state: FundCostPaymentFormState) {
  return {
    amount: state.amount ?? 0,
    counterparty_name: state.counterparty_name || null,
    counterparty_iban: state.counterparty_iban || null,
  };
}

export function documentLinks(
  documents: IntakeDocument[]
): FundTransactionDocumentLinkRequest[] {
  return documents.map(({ document_id, document_types }) => ({
    document_id,
    document_types,
  }));
}
